import { Select, TextInput } from '@octano/global-ui';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import SectionTitle from '../../../components/Text/SectionTitle';
import { TranslationsKeys } from '../../../locales/translations';

const CHILE_LOWER_CASE = 'chile';

type EducationalBackgroundPDISectionProps = {
  prefix: string;
  fieldNames?: {
    highSchool: string;
    countryHighSchool: string;
    regionHighSchool: string;
    communeHighSchool: string;
    typeHighSchool: string;
    graduationYear: string;
  };
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicEducationalBackgroundPDI = ({
  prefix,
  fieldNames = {
    highSchool: 'highSchool',
    countryHighSchool: 'countryHighSchool',
    regionHighSchool: 'regionHighSchool',
    communeHighSchool: 'communeHighSchool',
    typeHighSchool: 'typeHighSchool',
    graduationYear: 'graduationYear',
  },
}: EducationalBackgroundPDISectionProps) => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_FILE_PERSONAL_INFORMATION,
  );
  const { control, watch } = useFormContext();

  const [selectedCountry] = watch([fieldNames.countryHighSchool]);

  const isChile = useMemo(() => {
    return String(selectedCountry?.label)
      .toLowerCase()
      .includes(CHILE_LOWER_CASE);
  }, [selectedCountry?.label]);

  const fields = useMemo(() => {
    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={8}>
            <TextInput
              name={fieldNames.highSchool}
              label={t(`${prefix}.${fieldNames.highSchool}`)}
              control={control}
              disabled
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.countryHighSchool}
              label={t(`${prefix}.${fieldNames.countryHighSchool}`)}
              options={[]}
              control={control}
              disabled
            />
          </Col>
          {isChile ? (
            <>
              <Col className="pb-3" xs={12} lg={4}>
                <Select
                  name={fieldNames.regionHighSchool}
                  label={t(`${prefix}.${fieldNames.regionHighSchool}`)}
                  options={[]}
                  control={control}
                  disabled
                />
              </Col>
              <Col className="pb-3" xs={12} lg={4}>
                <Select
                  name={fieldNames.communeHighSchool}
                  label={t(`${prefix}.${fieldNames.communeHighSchool}`)}
                  options={[]}
                  control={control}
                  shouldUnregister
                  disabled
                />
              </Col>
            </>
          ) : (
            <></>
          )}

          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.graduationYear}
              label={t(`${prefix}.${fieldNames.graduationYear}`)}
              control={control}
              formatter="phone"
              disabled
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [
    fieldNames.countryHighSchool,
    fieldNames.communeHighSchool,
    fieldNames.regionHighSchool,
    fieldNames.graduationYear,
    fieldNames.highSchool,
    t,
    control,
    prefix,
    isChile,
  ]);

  return <>{fields}</>;
};

export default DynamicEducationalBackgroundPDI;
